import { useState } from 'react';
import { Plus, Check, Tag as TagIcon } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Tag } from '@/hooks/useTags';
import { TagBadge } from './TagBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { useFeature } from '@/components/FeatureGate';
import { UpgradePrompt } from '@/components/UpgradePrompt';
import { cn } from '@/lib/utils';

const FREE_TAG_LIMIT = 3;

interface TagSelectorProps {
  tags: Tag[];
  selectedTagId: string | null;
  onSelect: (tagId: string | null) => void;
  onCreateTag: (name: string, color: string) => Promise<Tag | null>;
  compact?: boolean;
}

const COLOR_OPTIONS = [
  '#22c55e', // green
  '#3b82f6', // blue
  '#a855f7', // purple
  '#f59e0b', // amber
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#ef4444', // red
  '#84cc16', // lime
  '#f97316', // orange
  '#6366f1', // indigo
];

export function TagSelector({ 
  tags, 
  selectedTagId, 
  onSelect, 
  onCreateTag,
  compact = false 
}: TagSelectorProps) {
  const isMobile = useIsMobile();
  const hasUnlimitedTags = useFeature('unlimitedTags');
  const customTagCount = tags.filter((t) => !t.is_preset).length;
  const atTagLimit = !hasUnlimitedTags && customTagCount >= FREE_TAG_LIMIT;
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(COLOR_OPTIONS[0]);

  const selectedTag = tags.find(t => t.id === selectedTagId);

  const handleSelect = (tagId: string) => {
    const nextId = selectedTagId === tagId ? null : tagId;
    // Close drawer first so Vaul doesn't glitch when parent re-renders with new task.tag_id
    setOpen(false);
    // Defer parent update to next tick so drawer close and state update don't conflict in one render
    queueMicrotask(() => onSelect(nextId));
  };

  const handleCreate = async () => {
    if (!newTagName.trim()) return;
    if (atTagLimit) return;
    const tag = await onCreateTag(newTagName.trim(), newTagColor);
    if (tag) {
      setNewTagName('');
      setShowCreate(false);
      setOpen(false);
      queueMicrotask(() => onSelect(tag.id));
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setShowCreate(false);
      setNewTagName('');
    }
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>
        {selectedTag ? (
          <button type="button" className="focus:outline-none">
            <TagBadge 
              name={selectedTag.name} 
              color={selectedTag.color}
              size={compact ? 'sm' : 'md'}
            />
          </button>
        ) : (
          <Button 
            type="button"
            variant="ghost" 
            size="sm"
            className={cn(
              "h-6 px-2 text-muted-foreground hover:text-foreground",
              compact && "h-5 px-1.5"
            )}
          >
            <TagIcon className="h-3 w-3 mr-1" />
            <span className="text-xs">Tag</span>
          </Button>
        )}
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className="text-center pb-2">
          <DrawerTitle>{showCreate ? 'Create New Tag' : 'Select Tag'}</DrawerTitle>
        </DrawerHeader>
        
        <div className="px-0 pb-4">
          {showCreate ? (
            <div className="space-y-4">
              <Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Tag name..."
                className="h-11 text-base"
                autoFocus={!isMobile}
              />
              <div className="flex flex-wrap gap-2 justify-center">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewTagColor(color)}
                    className={cn(
                      "w-8 h-8 rounded-full transition-all",
                      newTagColor === color && "ring-2 ring-offset-2 ring-offset-background ring-primary scale-110"
                    )}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 h-11"
                  onClick={() => setShowCreate(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="flex-1 h-11"
                  onClick={handleCreate}
                  disabled={!newTagName.trim()}
                >
                  Create
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {selectedTagId && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(false);
                    queueMicrotask(() => onSelect(null));
                  }}
                  className="w-full flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground hover:bg-accent rounded-xl transition-colors"
                >
                  Clear tag
                </button>
              )}
              <div className="grid grid-cols-2 gap-2">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelect(tag.id);
                    }}
                    className={cn(
                      "flex items-center justify-between px-4 py-3 rounded-xl transition-colors",
                      selectedTagId === tag.id ? "bg-accent" : "bg-muted/50 hover:bg-accent/50"
                    )}
                  >
                    <TagBadge name={tag.name} color={tag.color} />
                    {selectedTagId === tag.id && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </button>
                ))}
              </div>
              {atTagLimit ? (
                <UpgradePrompt
                  feature="unlimited custom tags"
                  description={`Basic includes ${FREE_TAG_LIMIT} tags. Upgrade for more.`}
                  compact
                />
              ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowCreate(true);
                }}
                className="w-full flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground hover:bg-accent rounded-xl transition-colors mt-3 border border-dashed border-border"
              >
                <Plus className="h-4 w-4" />
                Create new tag
              </button>
              )}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
